import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  FileText,
  MapPin,
  DollarSign,
  HelpCircle,
  RefreshCw,
  Send,
  Download,
  ExternalLink,
  Trash2,
  ArrowLeft,
  Sparkles,
  Loader,
  AlertCircle,
  Layers,
  Eye,
  Globe,
  Shield,
  ImageIcon,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import api from "../api/client";
import ScoreRing from "./ScoreRing";
import KeywordDetailModal from "./KeywordDetailModal";

const pageTypeConfig = {
  service_location: {
    label: "Service + Location",
    icon: MapPin,
    color: "text-blue-400",
    bg: "bg-blue-900/20",
    description: 'e.g. "Dental Office Construction in Scarsdale"',
  },
  service: {
    label: "Service Page",
    icon: FileText,
    color: "text-green-400",
    bg: "bg-green-900/20",
    description: 'e.g. "Bathroom Remodeling"',
  },
  cost_guide: {
    label: "Cost / Guide",
    icon: DollarSign,
    color: "text-amber-400",
    bg: "bg-amber-900/20",
    description: 'e.g. "How Much Does Dental Office Construction Cost"',
  },
  faq: {
    label: "FAQ Page",
    icon: HelpCircle,
    color: "text-purple-400",
    bg: "bg-purple-900/20",
    description: "Auto-generated FAQ with schema markup",
  },
};

const statusBadge = (status) => {
  const map = {
    queued: "bg-gray-800 text-gray-400",
    analyzing: "bg-blue-900/30 text-blue-400",
    generating: "bg-blue-900/30 text-blue-400",
    reviewing: "bg-amber-900/30 text-amber-400",
    publishing: "bg-blue-900/30 text-blue-400",
    published: "bg-green-900/30 text-green-400",
    failed: "bg-red-900/30 text-red-400",
    archived: "bg-gray-800 text-gray-500",
  };
  return map[status] || map.queued;
};

export default function LandingPages({ selectedSite }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | create | detail | bulk
  const [selectedPage, setSelectedPage] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [detailKeyword, setDetailKeyword] = useState(null);

  // Create form state
  const [form, setForm] = useState({
    page_type: "service_location",
    target_keyword: "",
    target_location: "",
    auto_publish: false,
    include_images: true,
  });
  const [bulkLocations, setBulkLocations] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Detail state
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("preview");
  const [publishing, setPublishing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [publishError, setPublishError] = useState("");
  const [updatingReview, setUpdatingReview] = useState(false);

  // Handle query params for pre-filling (FIX 1)
  useEffect(() => {
    const action = searchParams.get("action");
    const keyword = searchParams.get("keyword");

    if (action === "generate" && keyword) {
      const location = searchParams.get("location") || "";
      const pageType = searchParams.get("type") || "service_location";

      setForm({
        page_type: pageType,
        target_keyword: keyword,
        target_location: location,
        auto_publish: false,
        include_images: true,
      });
      setView("create");

      // Clear params so refreshing doesn't re-trigger
      setSearchParams({}, { replace: true });
    } else if (keyword && !action) {
      // Legacy: just keyword param
      setForm((prev) => ({ ...prev, target_keyword: keyword }));
      setView("create");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const fetchPages = () => {
    if (!selectedSite) return;
    api
      .get(`/websites/${selectedSite.id}/pages/`)
      .then((res) => setPages(res.data.results || res.data))
      .catch(() => setPages([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setView("list");
    setSelectedPage(null);
    setLoading(true);
    fetchPages();
  }, [selectedSite]);

  // Auto-refresh pages in progress
  useEffect(() => {
    const hasInProgress = pages.some((p) =>
      ["queued", "analyzing", "generating", "publishing"].includes(p.status)
    );
    if (!hasInProgress) return;
    const interval = setInterval(fetchPages, 8000);
    return () => clearInterval(interval);
  }, [pages, selectedSite]);

  const openDetail = (page) => {
    setDetailLoading(true);
    api
      .get(`/websites/${selectedSite.id}/pages/${page.id}/`)
      .then((res) => {
        setSelectedPage(res.data);
        setView("detail");
        setActiveTab("preview");
      })
      .catch(() => {})
      .finally(() => setDetailLoading(false));
  };

  const refreshDetail = () => {
    if (!selectedPage) return;
    api
      .get(`/websites/${selectedSite.id}/pages/${selectedPage.id}/`)
      .then((res) => setSelectedPage(res.data))
      .catch(() => {});
  };

  // Auto-refresh detail while processing
  useEffect(() => {
    if (!selectedPage) return;
    const active = ["queued", "analyzing", "generating", "publishing"].includes(
      selectedPage.status
    );
    if (!active) return;
    const pageId = selectedPage.id;
    const siteId = selectedSite?.id;
    const interval = setInterval(() => {
      api
        .get(`/websites/${siteId}/pages/${pageId}/`)
        .then((res) => setSelectedPage(res.data))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, [selectedPage?.id, selectedPage?.status, selectedSite?.id]);

  const handleCreate = async () => {
    if (!form.target_keyword.trim()) {
      setError("Keyword is required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/websites/${selectedSite.id}/pages/generate/`, {
        page_type: form.page_type,
        target_keyword: form.target_keyword.trim(),
        target_location: form.target_location.trim(),
        auto_publish: form.auto_publish,
        include_images: form.include_images,
      });
      setForm({ page_type: "service_location", target_keyword: "", target_location: "", auto_publish: false, include_images: true });
      setView("list");
      fetchPages();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create page");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkCreate = async () => {
    const locations = bulkLocations
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (!form.target_keyword.trim() || locations.length === 0) {
      setError("Keyword and at least one location required");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.post(`/websites/${selectedSite.id}/pages/bulk-generate/`, {
        page_type: form.page_type,
        target_keyword: form.target_keyword.trim(),
        locations,
        auto_publish: form.auto_publish,
        include_images: form.include_images,
      });
      setForm({ page_type: "service_location", target_keyword: "", target_location: "", auto_publish: false, include_images: true });
      setBulkLocations("");
      setView("list");
      fetchPages();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create pages");
    } finally {
      setSubmitting(false);
    }
  };

  const deletePage = (e, page) => {
    e.stopPropagation();
    api
      .delete(`/websites/${selectedSite.id}/pages/${page.id}/`)
      .then(() => {
        setPages((prev) => prev.filter((p) => p.id !== page.id));
        if (selectedPage?.id === page.id) {
          setView("list");
          setSelectedPage(null);
        }
      })
      .catch((err) => alert(err.response?.data?.error || err.response?.data?.detail || "Failed to delete page"));
  };

  const handlePublish = () => {
    setPublishing(true);
    setPublishError("");
    api
      .post(`/websites/${selectedSite.id}/pages/${selectedPage.id}/publish/`)
      .then(() => refreshDetail())
      .catch((err) => {
        const msg = err.response?.data?.error || "Failed to publish";
        setPublishError(msg);
      })
      .finally(() => setPublishing(false));
  };

  const handleRegenerate = () => {
    setRegenerating(true);
    api
      .post(`/websites/${selectedSite.id}/pages/${selectedPage.id}/regenerate/`)
      .then(() => {
        // Immediately update local state so the processing banner shows
        setSelectedPage((prev) => prev ? { ...prev, status: "queued" } : prev);
        setPages((prev) => prev.map((p) => p.id === selectedPage.id ? { ...p, status: "queued" } : p));
        setRegenerating(false);
      })
      .catch((err) => {
        alert(err.response?.data?.error || err.response?.data?.detail || "Failed to regenerate");
        setRegenerating(false);
      });
  };

  const updateReviewStatus = (newStatus) => {
    setUpdatingReview(true);
    api
      .patch(`/websites/${selectedSite.id}/pages/${selectedPage.id}/review-status/`, {
        review_status: newStatus,
      })
      .then(() => refreshDetail())
      .catch((err) => alert(err.response?.data?.error || "Failed to update review status"))
      .finally(() => setUpdatingReview(false));
  };

  const handleDownload = () => {
    api
      .get(
        `/websites/${selectedSite.id}/pages/${selectedPage.id}/download/`,
        { responseType: "blob" }
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", selectedPage.slug + ".html");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  const filtered = useMemo(() => {
    let data = [...pages];
    if (statusFilter !== "all") {
      data = data.filter((p) => p.status === statusFilter);
    }
    if (typeFilter !== "all") {
      data = data.filter((p) => p.page_type === typeFilter);
    }
    return data;
  }, [pages, statusFilter, typeFilter]);

  if (!selectedSite) {
    return (
      <div className="text-gray-500 text-center py-20">
        Select a website to generate landing pages
      </div>
    );
  }

  // --- DETAIL VIEW ---
  if (view === "detail" && selectedPage) {
    const isProcessing = ["queued", "analyzing", "generating", "publishing"].includes(
      selectedPage.status
    );
    const typeConf = pageTypeConfig[selectedPage.page_type] || pageTypeConfig.service;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setView("list"); setSelectedPage(null); }}
              className="p-2 hover:bg-dark-50/50 rounded-lg transition-colors"
            >
              <ArrowLeft size={18} className="text-gray-400" />
            </button>
            <div>
              <h2 className="text-lg font-semibold font-mono">
                {selectedPage.title || selectedPage.target_keyword}
              </h2>
              <div className="text-xs text-gray-500 flex items-center gap-2">
                <span className={`${typeConf.color}`}>{typeConf.label}</span>
                {selectedPage.target_location && (
                  <>
                    <span>·</span>
                    <span>{selectedPage.target_location}</span>
                  </>
                )}
                <span>·</span>
                <span>{selectedPage.website_domain}</span>
                {selectedPage.review_status && (
                  <>
                    <span>·</span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      selectedPage.review_status === "reviewed" ? "bg-green-900/30 text-green-400" :
                      selectedPage.review_status === "needs_edit" ? "bg-red-900/30 text-red-400" :
                      "bg-amber-900/30 text-amber-400"
                    }`}>
                      {selectedPage.review_status === "ai_draft" ? "AI Draft" :
                       selectedPage.review_status === "reviewed" ? "Reviewed" : "Needs Edit"}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {selectedPage.uniqueness_score != null && (
              <ScoreRing
                score={selectedPage.uniqueness_score}
                size={48}
                strokeWidth={4}
                label="Unique"
                color={selectedPage.uniqueness_score >= 70 ? undefined : selectedPage.uniqueness_score >= 60 ? "#f59e0b" : "#ef4444"}
              />
            )}
            {selectedPage.seo_score != null && (
              <ScoreRing score={selectedPage.seo_score} size={48} strokeWidth={4} label="SEO" />
            )}
            {selectedPage.status === "reviewing" && (
              <>
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating}
                  className="flex items-center gap-2 px-3 py-2 bg-dark-300 border border-dark-50 text-sm text-gray-300 hover:text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={14} className={regenerating ? "animate-spin" : ""} />
                  Regenerate
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-2 px-3 py-2 bg-dark-300 border border-dark-50 text-sm text-gray-300 hover:text-white rounded-lg transition-colors"
                >
                  <Download size={14} />
                  HTML
                </button>
                {selectedPage.uniqueness_score != null && selectedPage.uniqueness_score < 70 ? (
                  <button
                    onClick={() => {
                      if (window.confirm(`This page has a uniqueness score of ${selectedPage.uniqueness_score}%. Publishing similar content may hurt SEO rankings. Are you sure?`)) {
                        handlePublish();
                      }
                    }}
                    disabled={publishing}
                    title={`Uniqueness ${selectedPage.uniqueness_score}% — below 70% threshold`}
                    className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Send size={14} />
                    {publishing ? "Publishing..." : "Publish Anyway"}
                  </button>
                ) : (
                  <button
                    onClick={handlePublish}
                    disabled={publishing}
                    title="Publish to WordPress"
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Send size={14} />
                    {publishing ? "Publishing..." : "Publish to WP"}
                  </button>
                )}
              </>
            )}
            {selectedPage.status === "reviewing" && selectedPage.review_status !== "reviewed" && (
              <button
                onClick={() => updateReviewStatus("reviewed")}
                disabled={updatingReview}
                className="flex items-center gap-2 px-3 py-2 bg-green-900/30 border border-green-800/30 text-sm text-green-400 hover:bg-green-900/50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Shield size={14} />
                Mark Reviewed
              </button>
            )}
            {selectedPage.status === "reviewing" && selectedPage.review_status === "reviewed" && (
              <button
                onClick={() => updateReviewStatus("needs_edit")}
                disabled={updatingReview}
                className="flex items-center gap-2 px-3 py-2 bg-red-900/30 border border-red-800/30 text-sm text-red-400 hover:bg-red-900/50 rounded-lg transition-colors disabled:opacity-50"
              >
                <AlertCircle size={14} />
                Needs Edit
              </button>
            )}
            {selectedPage.published_url && (
              <>
                <button
                  onClick={() => {
                    api.post(`/websites/${selectedSite.id}/pages/${selectedPage.id}/request-indexing/`)
                      .then(() => refreshDetail());
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-900/30 text-blue-400 text-sm rounded-lg hover:bg-blue-900/50"
                  title="Request Google indexing"
                >
                  <Globe size={14} />
                  Index
                </button>
                <a
                  href={selectedPage.published_url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-green-900/30 text-green-400 text-sm rounded-lg"
                >
                  <ExternalLink size={14} />
                  View Live
                </a>
              </>
            )}
          </div>
        </div>

        {/* Processing indicator */}
        {isProcessing && (
          <div className="bg-blue-900/20 border border-blue-800/30 rounded-lg px-4 py-3 flex items-center gap-3">
            <RefreshCw size={16} className="text-blue-400 animate-spin" />
            <span className="text-sm text-blue-300">
              {selectedPage.status === "queued" && "Waiting in queue..."}
              {selectedPage.status === "analyzing" && "Analyzing top Google results..."}
              {selectedPage.status === "generating" && "AI is generating your landing page..."}
              {selectedPage.status === "publishing" && "Publishing to WordPress..."}
            </span>
          </div>
        )}

        {/* Error */}
        {selectedPage.status === "failed" && selectedPage.error_message && (
          <div className="bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-3">
            <div className="text-sm text-red-400 font-medium">Generation Failed</div>
            <div className="text-xs text-red-300 mt-1">{selectedPage.error_message}</div>
            <button
              onClick={handleRegenerate}
              className="mt-2 text-xs text-copper hover:text-copper-300"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Uniqueness warning */}
        {selectedPage.uniqueness_score != null && selectedPage.uniqueness_score < 60 && (
          <div className="bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-3 flex items-start gap-3">
            <AlertCircle size={18} className="text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm text-red-400 font-medium">
                Low uniqueness — may hurt SEO rankings
              </div>
              <div className="text-xs text-red-300 mt-1">
                Uniqueness score is {selectedPage.uniqueness_score}% (recommended: 70%+).
                This page shares too much content with other pages on your site. Consider regenerating
                with different local details, case studies, and section structure.
              </div>
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="mt-2 text-xs text-copper hover:text-copper-300 font-medium"
              >
                {regenerating ? "Regenerating..." : "Regenerate with more unique content"}
              </button>
            </div>
          </div>
        )}

        {selectedPage.uniqueness_score != null && selectedPage.uniqueness_score >= 60 && selectedPage.uniqueness_score < 70 && (
          <div className="bg-amber-900/20 border border-amber-800/30 rounded-lg px-4 py-3 flex items-start gap-3">
            <AlertCircle size={18} className="text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm text-amber-400 font-medium">
                Uniqueness below recommended threshold
              </div>
              <div className="text-xs text-amber-300 mt-1">
                Uniqueness score is {selectedPage.uniqueness_score}% (recommended: 70%+).
                You can still publish, but consider regenerating for more unique content.
              </div>
            </div>
          </div>
        )}

        {/* Publish error */}
        {publishError && (
          <div className="bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-3">
            <div className="text-sm text-red-400">{publishError}</div>
          </div>
        )}

        {/* Meta info */}
        {selectedPage.title && (
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4 space-y-2">
            <div>
              <span className="text-xs text-gray-500">Meta Title</span>
              <div className="text-sm text-blue-400 font-medium">{selectedPage.title}</div>
            </div>
            {selectedPage.meta_description && (
              <div>
                <span className="text-xs text-gray-500">Meta Description</span>
                <div className="text-sm text-gray-300">{selectedPage.meta_description}</div>
              </div>
            )}
          </div>
        )}

        {/* Image Sources */}
        {selectedPage.image_sources && selectedPage.image_sources.length > 0 && (
          <div className="bg-dark-300 border border-dark-50 rounded-xl p-4">
            <div className="text-xs text-gray-500 mb-2">Images Used</div>
            <div className="flex flex-wrap gap-2">
              {selectedPage.image_sources.map((img, i) => (
                <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-dark/50 rounded-lg text-xs">
                  <span className={`px-1.5 py-0.5 rounded font-medium ${
                    img.source === "wordpress" ? "bg-blue-900/20 text-blue-400" :
                    img.source === "library" ? "bg-copper/15 text-copper" :
                    "bg-green-900/20 text-green-400"
                  }`}>
                    {img.source === "wordpress" ? "WP Media" : img.source === "library" ? "Library" : "Unsplash"}
                  </span>
                  <span className="text-gray-400 truncate max-w-[200px]">{img.alt_text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-dark-300 border border-dark-50 rounded-lg p-1">
          {[
            { key: "preview", label: "Preview", icon: Eye },
            { key: "html", label: "HTML Source", icon: FileText },
            { key: "blueprint", label: "Blueprint", icon: Sparkles },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-colors ${
                activeTab === key
                  ? "bg-dark-50 text-copper"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-6">
          {activeTab === "preview" &&
            (selectedPage.full_html || selectedPage.content_html ? (
              <iframe
                srcDoc={selectedPage.full_html || `<!DOCTYPE html><html><head><style>body{font-family:system-ui,sans-serif;line-height:1.6;padding:2rem;max-width:900px;margin:0 auto;color:#333}</style></head><body>${selectedPage.content_html}</body></html>`}
                title="Landing Page Preview"
                className="w-full rounded-lg border border-dark-50 bg-white"
                style={{ minHeight: "600px", height: "80vh" }}
                sandbox="allow-same-origin allow-scripts"
              />
            ) : (
              <div className="text-gray-500 text-center py-12">
                {isProcessing ? "Content will appear here once generated..." : "No content generated yet"}
              </div>
            ))}

          {activeTab === "html" &&
            (selectedPage.full_html ? (
              <pre className="text-xs text-gray-300 font-mono overflow-x-auto whitespace-pre-wrap max-h-[600px] overflow-y-auto">
                {selectedPage.full_html}
              </pre>
            ) : (
              <div className="text-gray-500 text-center py-12">
                {isProcessing ? "HTML being generated..." : "No HTML available"}
              </div>
            ))}

          {activeTab === "blueprint" &&
            (selectedPage.blueprint ? (
              <div className="space-y-4">
                {selectedPage.blueprint.title && (
                  <div>
                    <span className="text-xs text-gray-500">Planned Title</span>
                    <div className="text-sm font-medium text-gray-200">{selectedPage.blueprint.title}</div>
                  </div>
                )}
                {selectedPage.blueprint.unique_angle && (
                  <div>
                    <span className="text-xs text-gray-500">Unique Angle</span>
                    <div className="text-sm text-gray-300">{selectedPage.blueprint.unique_angle}</div>
                  </div>
                )}
                {selectedPage.blueprint.outline && (
                  <div>
                    <span className="text-xs text-gray-500">Content Outline</span>
                    <div className="mt-2 space-y-1.5">
                      {selectedPage.blueprint.outline.map((section, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-gray-200 font-medium">{section.heading}</span>
                          {section.notes && (
                            <span className="text-gray-500 text-xs mt-0.5">— {section.notes}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedPage.blueprint.keywords_to_include && (
                  <div>
                    <span className="text-xs text-gray-500">Keywords to Include</span>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {selectedPage.blueprint.keywords_to_include.map((kw, i) => (
                        <span key={i} className="px-2 py-0.5 bg-dark-50 rounded text-xs text-gray-300">
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500 text-center py-12">
                {isProcessing ? "Blueprint being generated..." : "No blueprint available"}
              </div>
            ))}
        </div>
      </div>
    );
  }

  // --- CREATE / BULK VIEW ---
  if (view === "create" || view === "bulk") {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setView("list")}
            className="p-2 hover:bg-dark-50/50 rounded-lg transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-400" />
          </button>
          <h2 className="text-lg font-semibold">
            {view === "bulk" ? "Bulk Generate Pages" : "Generate Landing Page"}
          </h2>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); view === "bulk" ? handleBulkCreate() : handleCreate(); }}
          className="bg-dark-300 border border-dark-50 rounded-xl p-6 space-y-5"
        >
          {/* Page Type */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Page Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(pageTypeConfig).map(([key, conf]) => {
                const Icon = conf.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm({ ...form, page_type: key })}
                    className={`text-left p-3 rounded-lg border transition-colors ${
                      form.page_type === key
                        ? "border-copper bg-copper/10"
                        : "border-dark-50 hover:border-gray-600"
                    }`}
                  >
                    <div className={`flex items-center gap-2 text-sm font-medium ${form.page_type === key ? "text-copper" : "text-gray-300"}`}>
                      <Icon size={14} />
                      {conf.label}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{conf.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Target Keyword */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Target Keyword *
            </label>
            <input
              type="text"
              value={form.target_keyword}
              onChange={(e) => setForm({ ...form, target_keyword: e.target.value })}
              placeholder='e.g. "dental office construction"'
              className="w-full bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50"
            />
          </div>

          {/* Location (single) or Locations (bulk) */}
          {view === "bulk" ? (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Locations (one per line)
              </label>
              <textarea
                value={bulkLocations}
                onChange={(e) => setBulkLocations(e.target.value)}
                placeholder={"Scarsdale, NY\nWhite Plains, NY\nNew Rochelle, NY\nYonkers, NY\nMount Vernon, NY"}
                rows={6}
                className="w-full bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50 font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                A separate page will be generated for each location
              </p>
              {/* Quick-add from website locations */}
              {selectedSite.target_locations && selectedSite.target_locations.length > 0 && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-gray-500">Quick add:</span>
                  {selectedSite.target_locations.map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      onClick={() => {
                        const current = bulkLocations.split("\n").map((l) => l.trim()).filter(Boolean);
                        if (!current.includes(loc)) {
                          setBulkLocations(
                            [...current, loc].join("\n")
                          );
                        }
                      }}
                      className="text-xs px-2 py-1 bg-dark-50/50 rounded text-copper hover:bg-copper/10 transition-colors"
                    >
                      {loc}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Target Location
              </label>
              <input
                type="text"
                value={form.target_location}
                onChange={(e) => setForm({ ...form, target_location: e.target.value })}
                placeholder='e.g. "Scarsdale, NY"'
                className="w-full bg-dark border border-dark-50 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-copper/50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional — adds geo-targeting to the page
              </p>
            </div>
          )}

          {/* Toggles */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.auto_publish}
                onChange={(e) => setForm({ ...form, auto_publish: e.target.checked })}
                className="w-4 h-4 rounded border-dark-50 bg-dark text-copper focus:ring-copper/50"
              />
              <div>
                <span className="text-sm text-gray-300">Auto-publish when ready</span>
                <p className="text-xs text-gray-500">Automatically publish to WordPress when SEO score is 70+</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.include_images}
                onChange={(e) => setForm({ ...form, include_images: e.target.checked })}
                className="w-4 h-4 rounded border-dark-50 bg-dark text-copper focus:ring-copper/50"
              />
              <div>
                <span className="text-sm text-gray-300">Include images (recommended for SEO)</span>
                <p className="text-xs text-gray-500">Auto-find and insert relevant images from WP media library or Unsplash</p>
              </div>
            </label>
          </div>

          {error && (
            <div className="text-red-400 text-sm bg-red-900/20 border border-red-800/30 rounded-lg px-4 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-copper hover:bg-copper-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <Sparkles size={16} />
            {submitting
              ? "Starting..."
              : view === "bulk"
              ? `Generate ${bulkLocations.split("\n").filter((l) => l.trim()).length || 0} Pages`
              : "Generate Landing Page"}
          </button>

          <p className="text-xs text-gray-500 text-center">
            Pipeline: analyze top Google results → generate blueprint → create
            optimized content → find images → build standalone HTML
          </p>
        </form>
      </div>
    );
  }

  // --- LIST VIEW ---
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Landing Pages</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView("bulk")}
            className="flex items-center gap-2 px-4 py-2 bg-dark-300 border border-dark-50 text-gray-300 hover:text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Layers size={14} />
            Bulk Generate
          </button>
          <button
            onClick={() => setView("create")}
            className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Plus size={14} />
            New Page
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex gap-2">
          {["all", "queued", "generating", "reviewing", "published", "failed"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                statusFilter === s
                  ? "bg-copper/20 text-copper"
                  : "bg-dark-300 text-gray-400 hover:text-white"
              }`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-dark-300 border border-dark-50 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:border-copper/50"
        >
          <option value="all">All Types</option>
          {Object.entries(pageTypeConfig).map(([key, conf]) => (
            <option key={key} value={key}>
              {conf.label}
            </option>
          ))}
        </select>
      </div>

      {/* Pages table */}
      {loading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-12 bg-dark-300 rounded-lg" />
          <div className="h-12 bg-dark-300 rounded-lg" />
          <div className="h-12 bg-dark-300 rounded-lg" />
        </div>
      ) : (
        <div className="bg-dark-300 border border-dark-50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-dark-50">
                  <th className="text-left py-2.5 px-3 font-medium">Keyword</th>
                  <th className="text-left py-2.5 px-3 font-medium">Location</th>
                  <th className="text-left py-2.5 px-3 font-medium">Type</th>
                  <th className="text-left py-2.5 px-3 font-medium">Status</th>
                  <th className="text-left py-2.5 px-3 font-medium">Review</th>
                  <th className="text-left py-2.5 px-3 font-medium">Score</th>
                  <th className="text-left py-2.5 px-3 font-medium">Unique</th>
                  <th className="text-left py-2.5 px-3 font-medium">Index</th>
                  <th className="text-left py-2.5 px-3 font-medium">Created</th>
                  <th className="py-2.5 px-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((page) => {
                  const typeConf = pageTypeConfig[page.page_type] || pageTypeConfig.service;
                  const TypeIcon = typeConf.icon;
                  const isActive = ["queued", "analyzing", "generating", "publishing"].includes(page.status);
                  return (
                    <tr
                      key={page.id}
                      onClick={() => openDetail(page)}
                      className="border-b border-dark-50/50 hover:bg-dark-50/30 transition-colors cursor-pointer"
                    >
                      <td className="py-2.5 px-3 font-mono text-gray-200">
                        <button
                          onClick={(e) => { e.stopPropagation(); setDetailKeyword(page.target_keyword); }}
                          className="hover:text-copper transition-colors text-left"
                        >
                          {page.target_keyword}
                        </button>
                      </td>
                      <td className="py-2.5 px-3 text-gray-400">
                        {page.target_location || "—"}
                      </td>
                      <td className="py-2.5 px-3">
                        <span className={`inline-flex items-center gap-1 text-xs ${typeConf.color}`}>
                          <TypeIcon size={11} />
                          {typeConf.label}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusBadge(page.status)}`}
                        >
                          {isActive && <Loader size={10} className="animate-spin" />}
                          {page.status}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        {page.review_status ? (
                          <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                            page.review_status === "reviewed" ? "bg-green-900/20 text-green-400" :
                            page.review_status === "needs_edit" ? "bg-red-900/20 text-red-400" :
                            "bg-amber-900/20 text-amber-400"
                          }`}>
                            {page.review_status === "ai_draft" ? "AI Draft" :
                             page.review_status === "reviewed" ? "Reviewed" : "Needs Edit"}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-gray-400">
                        {page.seo_score != null ? page.seo_score : "—"}
                      </td>
                      <td className="py-2.5 px-3 font-mono text-gray-400">
                        {page.uniqueness_score != null ? (
                          <span className={page.uniqueness_score < 70 ? "text-red-400" : "text-green-400"}>
                            {page.uniqueness_score}%
                          </span>
                        ) : "—"}
                      </td>
                      <td className="py-2.5 px-3">
                        {page.indexing_status && page.indexing_status !== "pending" ? (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            page.indexing_status === "indexed" ? "bg-green-900/20 text-green-400" :
                            page.indexing_status === "submitted" ? "bg-blue-900/20 text-blue-400" :
                            page.indexing_status === "failed" ? "bg-red-900/20 text-red-400" :
                            "text-gray-500"
                          }`}>
                            {page.indexing_status}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="py-2.5 px-3 text-xs text-gray-500">
                        {new Date(page.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1">
                          {page.published_url && (
                            <a
                              href={page.published_url}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="p-1 text-gray-500 hover:text-green-400 transition-colors"
                              title="View live"
                            >
                              <ExternalLink size={12} />
                            </a>
                          )}
                          <button
                            onClick={(e) => deletePage(e, page)}
                            className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                            title="Delete page"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!filtered.length && (
                  <tr>
                    <td colSpan={10} className="py-12 text-center text-gray-500">
                      {pages.length
                        ? "No pages match your filters"
                        : "No landing pages generated yet. Click \"New Page\" to start."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detailKeyword && (
        <KeywordDetailModal
          keyword={detailKeyword}
          selectedSite={selectedSite}
          onClose={() => setDetailKeyword(null)}
        />
      )}
    </div>
  );
}
